/**
 * RetirementPlanningCard Component
 * Dashboard card for retirement planning.
 *
 * Simplified view: slider + inputs + heatmap + goal calculator.
 * No events panel (works without events entitlement).
 * Age derived from birth year when available.
 */

import { useState, useMemo, useCallback, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import Card, { CardHeader } from '@/components/ui/Card';
import {
  useRetirementInputs,
  useRetirementNetWorth,
  useUpdateRetirementInputs,
  useBirthYear,
  useUpdateBirthYear,
} from '@/hooks/useRetirementPlanning';
import { useHasEntitlement } from '@/hooks/useBilling';
import { buildProjectionGrid, calculateRequiredMonthlySavings } from '@/utils/retirementCalculations';
import { centsToDollars } from '@/utils/format';
import { cn } from '@/utils/cn';
import { Select } from '@/components/ui';
import type { RetirementInputsUpdate } from '@/types/retirement';

import RetirementSkeleton from './RetirementSkeleton';
import RetirementSetupPrompt from './RetirementSetupPrompt';
import RetirementSummaryLine from './RetirementSummaryLine';
import RetirementInputPanel from './RetirementInputPanel';
import RetirementHeatmap from './RetirementHeatmap';
import HeatmapLegend from './HeatmapLegend';
import RetirementDisclaimer from './RetirementDisclaimer';
import RetirementAgeSlider from '@/components/retirement/RetirementAgeSlider';
import GoalCalculator from '@/components/landing/GoalCalculator';

// ============================================================================
// Default Values for First-Time Setup
// ============================================================================

const DEFAULT_RETIREMENT_AGE = 65;

const DEFAULT_INPUTS: RetirementInputsUpdate = {
  currentAge: 35,
  retirementAge: DEFAULT_RETIREMENT_AGE,
  netWorthAdjustmentCents: 0,
  socialSecurityAnnualCents: 2_400_000,
  pensionAnnualCents: 0,
  otherRetirementIncomeCents: 0,
  retirementExpensesAnnualCents: 6_000_000,
  currentSavingsAnnualCents: 1_200_000,
};

// ============================================================================
// Main Component
// ============================================================================

export default function RetirementPlanningCard() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [retirementAgeOverride, setRetirementAgeOverride] = useState<number | null>(null);

  const { hasEntitlement, isLoading: entitlementLoading } = useHasEntitlement(
    'feature:retirement_planning'
  );

  const { data: inputs, isLoading: inputsLoading } = useRetirementInputs();
  const { data: netWorth, isLoading: netWorthLoading } = useRetirementNetWorth();
  const { birthYear, isLoading: birthYearLoading } = useBirthYear();
  const updateMutation = useUpdateRetirementInputs();
  const updateBirthYear = useUpdateBirthYear();

  const birthYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear - 18;
    const options: { value: string; label: string }[] = [];
    for (let year = maxYear; year >= 1930; year--) {
      options.push({ value: year.toString(), label: year.toString() });
    }
    return options;
  }, []);

  const handleBirthYearChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    const year = parseInt(e.target.value, 10);
    if (!isNaN(year)) updateBirthYear.mutate(year);
  }, [updateBirthYear]);

  const isLoading = inputsLoading || netWorthLoading || entitlementLoading || birthYearLoading;
  const hasInputs = inputs !== null && inputs !== undefined;

  // Derive currentAge from birth year, fall back to stored value
  const currentAge = birthYear != null
    ? new Date().getFullYear() - birthYear
    : (hasInputs ? inputs.currentAge : null);

  // Retirement age: slider override > stored > default
  const retirementAge = retirementAgeOverride ?? (hasInputs ? inputs.retirementAge : DEFAULT_RETIREMENT_AGE);

  // Build effective inputs with derived ages
  const effectiveInputs = useMemo(() => {
    if (!hasInputs || currentAge == null) return null;
    return { ...inputs, currentAge, retirementAge };
  }, [hasInputs, inputs, currentAge, retirementAge]);

  const isAlreadyRetired = effectiveInputs != null && effectiveInputs.currentAge >= effectiveInputs.retirementAge;

  const totalNetWorthCents = effectiveInputs && netWorth
    ? netWorth.totalCents + (effectiveInputs.netWorthAdjustmentCents ?? 0)
    : 0;

  const projections = effectiveInputs && netWorth
    ? buildProjectionGrid(effectiveInputs, totalNetWorthCents)
    : null;

  const goalResults = useMemo(() => {
    if (!effectiveInputs || !netWorth) return null;
    return calculateRequiredMonthlySavings(effectiveInputs, totalNetWorthCents, retirementAge);
  }, [effectiveInputs, netWorth, totalNetWorthCents, retirementAge]);

  const currentMonthlySavings = effectiveInputs
    ? centsToDollars(effectiveInputs.currentSavingsAnnualCents) / 12
    : 0;

  // Handle retirement age slider change
  const handleRetirementAgeChange = (age: number) => {
    setRetirementAgeOverride(age);

    if (currentAge != null && hasInputs) {
      updateMutation.mutate({
        currentAge,
        retirementAge: age,
        netWorthAdjustmentCents: inputs.netWorthAdjustmentCents,
        socialSecurityAnnualCents: inputs.socialSecurityAnnualCents,
        pensionAnnualCents: inputs.pensionAnnualCents,
        otherRetirementIncomeCents: inputs.otherRetirementIncomeCents,
        retirementExpensesAnnualCents: inputs.retirementExpensesAnnualCents,
        currentSavingsAnnualCents: inputs.currentSavingsAnnualCents,
        socialSecurityStartAge: inputs.socialSecurityStartAge,
        pensionStartAge: inputs.pensionStartAge,
        otherIncomeStartAge: inputs.otherIncomeStartAge,
        workingIncomeAnnualCents: inputs.workingIncomeAnnualCents,
      });
    }
  };

  const handleInputUpdate = (values: RetirementInputsUpdate) => {
    if (currentAge != null) {
      updateMutation.mutate({ ...values, currentAge, retirementAge });
    } else {
      updateMutation.mutate(values);
    }
  };

  const handleSetup = async () => {
    const setupAge = currentAge ?? 35;
    await updateMutation.mutateAsync({
      ...DEFAULT_INPUTS,
      currentAge: setupAge,
      retirementAge: DEFAULT_RETIREMENT_AGE,
    });
  };

  if (!entitlementLoading && !hasEntitlement) {
    return null;
  }

  return (
    <Card>
      <CardHeader
        title="Retirement Coverage"
        action={
          hasInputs && (
            <div className="flex items-center gap-2">
              <Link
                to="/planning"
                className={cn(
                  'inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md',
                  'text-primary-600 hover:text-primary-700 hover:bg-primary-50',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
              >
                View Full Plan
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className={cn(
                  'p-1.5 rounded-md text-gray-500',
                  'hover:text-gray-700 hover:bg-gray-100',
                  'focus:outline-none focus:ring-2 focus:ring-primary-500'
                )}
                aria-label={isExpanded ? 'Collapse retirement coverage' : 'Expand retirement coverage'}
                aria-expanded={isExpanded}
              >
                <svg
                  className={cn('w-5 h-5 transition-transform duration-200', isExpanded && 'rotate-180')}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )
        }
      />

      {isLoading ? (
        <RetirementSkeleton />
      ) : !hasInputs ? (
        <RetirementSetupPrompt onSetup={handleSetup} isLoading={updateMutation.isPending} />
      ) : (
        <>
          {/* Collapsed state: show summary line */}
          {!isExpanded && effectiveInputs && (
            <RetirementSummaryLine inputs={effectiveInputs} netWorth={netWorth ?? null} />
          )}

          {/* Retirement age slider (always visible when has inputs) */}
          {isExpanded && (
            <div className="mb-6">
              <RetirementAgeSlider
                retirementAge={retirementAge}
                onRetirementAgeChange={handleRetirementAgeChange}
              />
            </div>
          )}

          <div className={cn('grid gap-6', isExpanded ? 'lg:grid-cols-2' : '')}>
            {/* Input panel - only shown when expanded */}
            {isExpanded && effectiveInputs && (
              <div className="lg:border-r lg:border-gray-200 lg:pr-6 space-y-6">
                {/* Birth year selector */}
                <Select
                  label="Birth Year"
                  id="dashboard-birth-year"
                  value={birthYear?.toString() ?? ''}
                  onChange={handleBirthYearChange}
                  options={birthYearOptions}
                  placeholder="Select year"
                />
                <RetirementInputPanel
                  inputs={effectiveInputs}
                  isAlreadyRetired={isAlreadyRetired}
                  onUpdate={handleInputUpdate}
                  variant="dashboard"
                />
              </div>
            )}

            {/* Heatmap - always shown */}
            <div>
              <RetirementHeatmap projections={projections} />
              <HeatmapLegend />
            </div>
          </div>

          {/* Goal Calculator */}
          {effectiveInputs && (
            <GoalCalculator
              goalRetirementAge={retirementAge}
              onGoalRetirementAgeChange={handleRetirementAgeChange}
              currentAge={effectiveInputs.currentAge}
              currentMonthlySavings={currentMonthlySavings}
              goalResults={goalResults}
            />
          )}
        </>
      )}

      <RetirementDisclaimer />
    </Card>
  );
}
